// src/components/settings/catalog/useCatalogSearch.js
// CATALOG SCREEN VERIFIER: catscreen-7a2c4e91 — Settings › Catalog, Stage 2.
//
// The Catalog list: searchCatalogOffice, 50 rows at a time. The server can return a short (even
// empty) page with "more" waiting when matches are sparse (several words, or a word inside a
// folder), so each "page" keeps asking until it has 50 rows or the list ends — up to MAX_CALLS
// asks, then "Show more" picks up where it stopped. Typing waits 300 ms before searching, and an
// answer to an older search is dropped.
//
// Also: useFolderCounts — one tiny search per folder (1 row + its count) for the folder column.
import { useCallback, useEffect, useRef, useState } from 'react';
import { searchCatalogOffice, friendlyError } from './catalogApi';

const PAGE = 50;
const MAX_CALLS = 8;
const LOAD_ERROR = "Couldn't load your catalog. Try again.";

export function useCatalogSearch(params) {
    const { query, folderId, type, archived, favoritesOnly } = params; // favoritesOnly: catscreen-7a2c4e91 Stage 4
    const [state, setState] = useState({
        items: [], count: null, countIsExact: null, nextCursor: null, loading: true, loadingMore: false, error: '',
    });
    const reqId = useRef(0);
    const [debouncedQuery, setDebouncedQuery] = useState(query);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(query), 300);
        return () => clearTimeout(t);
    }, [query]);

    const base = useCallback(() => {
        const input = {};
        if (debouncedQuery && debouncedQuery.trim()) input.query = debouncedQuery.trim();
        if (folderId) input.folderId = folderId;
        if (type) input.type = type;
        if (archived) input.archived = true;
        if (favoritesOnly) input.favoritesOnly = true;
        return input;
    }, [debouncedQuery, folderId, type, archived, favoritesOnly]);

    // Asks until `want` more rows arrive or the list ends. Returns what it found.
    const fetchRows = useCallback(async (id, cursor, want, withCount) => {
        let got = [];
        let next = cursor;
        let first = null;
        for (let calls = 0; calls < MAX_CALLS; calls++) {
            const res = await searchCatalogOffice({ ...base(), pageSize: Math.min(PAGE, want - got.length), withCount: withCount && calls === 0, ...(next ? { cursor: next } : {}) });
            if (id !== reqId.current) return null;
            if (!first) first = res;
            got = got.concat(res.items || []);
            next = res.nextCursor || null;
            if (got.length >= want || !next) break;
            setState((s) => (id === reqId.current ? { ...s, searching: true } : s));
        }
        return { got, next, first };
    }, [base]);

    const load = useCallback(async () => {
        const id = ++reqId.current;
        setState((s) => ({ ...s, loading: true, error: '', searching: true }));
        try {
            const r = await fetchRows(id, null, PAGE, true);
            if (!r) return;
            setState({ items: r.got, count: r.first.count, countIsExact: r.first.countIsExact, nextCursor: r.next, loading: false, loadingMore: false, error: '', searching: false });
        } catch (err) {
            if (id !== reqId.current) return;
            console.error('Catalog: search failed', err);
            setState((s) => ({ ...s, loading: false, searching: false, error: friendlyError(err, LOAD_ERROR) }));
        }
    }, [fetchRows]);

    useEffect(() => { load(); }, [load]);

    const showMore = useCallback(async () => {
        const id = reqId.current;
        const cursor = state.nextCursor;
        if (!cursor) return;
        setState((s) => ({ ...s, loadingMore: true, error: '', searching: true }));
        try {
            const r = await fetchRows(id, cursor, PAGE, false);
            if (!r) return;
            setState((s) => ({ ...s, items: s.items.concat(r.got.filter((x) => !s.items.some((y) => y.id === x.id))), nextCursor: r.next, loadingMore: false, searching: false }));
        } catch (err) {
            if (id !== reqId.current) return;
            console.error('Catalog: show more failed', err);
            setState((s) => ({ ...s, loadingMore: false, searching: false, error: friendlyError(err, LOAD_ERROR) }));
        }
    }, [fetchRows, state.nextCursor]);

    // After a save: change the row in place, add a new one at the top, or take one out.
    const patchItem = useCallback((row) => setState((s) => ({ ...s, items: s.items.map((x) => (x.id === row.id ? { ...x, ...row } : x)) })), []);
    const addItem = useCallback((row) => setState((s) => ({
        ...s, items: [row, ...s.items.filter((x) => x.id !== row.id)], count: typeof s.count === 'number' ? s.count + 1 : s.count,
    })), []);
    const removeItem = useCallback((id) => setState((s) => ({
        ...s, items: s.items.filter((x) => x.id !== id), count: typeof s.count === 'number' && s.items.some((x) => x.id === id) ? Math.max(0, s.count - 1) : s.count,
    })), []);

    // filter / filterKey (Stage 4): exactly what the list is showing, for "select all matching" — the
    // server works out the same items from the same filter.
    const filter = base();
    return { ...state, typingQuery: debouncedQuery !== query, showMore, retry: load, patchItem, addItem, removeItem, filter, filterKey: JSON.stringify(filter) };
}

// ─── folder counts ──────────────────────────────────────────────────────────
const COUNT_CONCURRENCY = 4;

/** keys: [{ key, input }] — input is a searchCatalogOffice filter. → { counts: {key: n}, refresh } */
export function useFolderCounts(keys) {
    const [counts, setCounts] = useState({});
    const cache = useRef(new Map());
    const generation = useRef(0);
    const [tick, setTick] = useState(0);
    const keyText = keys.map((k) => k.key).join('|');

    useEffect(() => {
        const gen = generation.current;
        const todo = keys.filter((k) => !cache.current.has(k.key));
        if (!todo.length) return undefined;
        let i = 0;
        let alive = true;
        const worker = async () => {
            while (alive && i < todo.length) {
                const k = todo[i++];
                cache.current.set(k.key, null);
                try {
                    const r = await searchCatalogOffice({ ...k.input, pageSize: 1, withCount: true });
                    if (!alive || gen !== generation.current) return;
                    cache.current.set(k.key, r.countIsExact ? r.count : null);
                    setCounts((c) => ({ ...c, [k.key]: r.countIsExact ? r.count : null }));
                } catch (err) {
                    cache.current.delete(k.key); // a count is a nicety: no error shown, tried again next time
                }
            }
        };
        Promise.all(Array.from({ length: Math.min(COUNT_CONCURRENCY, todo.length) }, worker));
        return () => {
            alive = false;
            // Counts still on their way are forgotten, so the next run asks for them again.
            for (const k of todo) if (cache.current.get(k.key) === null) cache.current.delete(k.key);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keyText, tick]);

    // After a save: forget every count (a little later, once the server has caught up).
    const refresh = useCallback(() => {
        setTimeout(() => {
            generation.current++;
            cache.current = new Map();
            setCounts({});
            setTick((t) => t + 1);
        }, 2500);
    }, []);

    return { counts, refresh };
}
